/**
 * Vuex module for conferences.
 */
import debug from 'debug'
import API from '@/api'
import events from 'Store/events'
import Conference from 'Store/class/conference'
import * as RMQClient from '@/rmqclient'

const dbg = debug('store:action:confbridge:')

const actions = {
  init: function ({ commit, rootGetters }) {
    dbg('Init application: conferences and members')
    return API.getConferences(0, state.pagination.perPage).then(resp => {
      commit('RESET')
      resp.data.data.forEach(conf => {
        commit('ADD_NEW', conf)
        RMQClient.sendCmd({ action: 'ConfbridgeList', conference: conf.confno })
      })
      commit('INIT_PAGINATION', resp.data.total)

      API.getAllWatchedConferences().then(resp => {
        resp.data.forEach(room => {
          commit('ADD_WATCHED', room)
        })
      })

      if (rootGetters['permissions/personalConfRead']) {
        dbg('Init application: personal conference')
        return API.getPersonalConference().then(resp => {
          if (resp.data.message && !resp.data.confno) {
            dbg(`No persnonal conferences: ${resp.data.message}`)
            return
          }
          if (!resp.data.disabled) {
            commit('ADD_PERSONAL_CONF', resp.data)
          }
        })
      }
    }).catch(err => {
      dbg(err)
      const msg = API.getException(err) || 'Failed to get conferences from API.'
      events.pubError(msg)
    })
  },

  pageLoad: function ({ commit, state }, page) {
    return new Promise((resolve, reject) => {
      const start = (page - 1) * state.pagination.perPage
      return API.getConferences(start, state.pagination.perPage).then(resp => {
        commit('RESET_SKIP_PERSONAL')
        resp.data.data.forEach(conf => {
          commit('ADD_NEW', conf)
          RMQClient.sendCmd({ action: 'ConfbridgeList', conference: conf.confno })
        })
        commit('SET_PAGE', { page: page, start: start })
        resolve()
      }).catch(err => {
        reject(err.response.data)
      })
    })
  },

  setActive: function ({ commit }, room) {
    commit('SET_ACTIVE', room)
  },

  memPaneShow: function ({ commit }, enable) {
    commit('SET_PANE_SHOW', enable)
  },

  toggleWatch: function ({ commit }, params) {
    return API.watchConference(params.conference.id, params.watch).then(() => {
      if (params.watch) {
        commit('ADD_WATCHED', params.conference)
      } else {
        commit('REMOVE_WATCHED', params.conference)
      }
    })
  },

  updatePersonalConference: function ({ commit }, config) {
    commit('UPDATE_PERSONAL_CONF', config)
  }
}

const mutations = {
  ADD_NEW (state, data) {
    state.conferences.push(new Conference(data))
  },

  ADD_PERSONAL_CONF (state, data) {
    const conf = new Conference(data)
    conf.setPersonal()
    state.conferences.push(conf)
  },

  ADD_WATCHED (state, room) {
    if (state.watched.find(r => r.id === room.id)) {
      dbg(`Room ${room.confno} is already watched`)
      return
    }
    state.watched.push(new Conference(room))
  },

  REMOVE_WATCHED (state, room) {
    const idx = state.watched.findIndex(r => r.id === room.id)
    if (idx === -1) {
      dbg(`Room ${room.confno} is not watched`)
      return
    }
    state.watched.splice(idx, 1)
  },

  ROUTE_ACTIONS (state, data) {
    const conference = Conference.find(state.conferences, data.conference)
    const watchedConference = Conference.find(state.watched, data.conference)
    if (!conference) return // conference is not here
    switch (data.event) {
      case 'ConfbridgeList':
      case 'ConfbridgeJoin':
        conference.addMember(data)
        if (watchedConference) watchedConference.addMember(data)
        break
      case 'ConfbridgeLeave':
        conference.delMember(data)
        if (watchedConference) watchedConference.delMember(data)
        break
      case 'ConfbridgeTalking':
        conference.memberTalkig(data)
        if (watchedConference) watchedConference.memberTalkig(data)
        break
      case 'ConfbridgeMute':
      case 'ConfbridgeUnmute':
        conference.memberMute(data)
        if (watchedConference) watchedConference.memberMute(data)
        break
      case 'ConfbridgeListComplete':
        dbg(`Added ${data.listitem} member to conferece`)
        break
      default:
        dbg(`Do not know how to route event "${data.event}"`)
        break
    }
  },

  INIT_PAGINATION (state, total) {
    state.pagination.total = total
    state.pagination.pages = Math.ceil(total / state.pagination.perPage)
    state.pagination.curPage = 1
    state.pagination.start = 0
    state.pagination.limit = state.pagination.perPage
  },

  SET_PAGE (state, { page, start }) {
    state.pagination.curPage = page
    state.pagination.start = start
  },

  SET_ACTIVE (state, room) {
    state.activeRoom = room
  },

  SET_PANE_SHOW (state, val) {
    state.memPaneShow = val
  },

  TOGGLE_WATCH (state, room) {
    room.watch = !room.watch
  },

  RESET (state) {
    state.conferences.splice(0)
    state.watched.splice(0)
  },

  RESET_SKIP_PERSONAL (state) {
    const idx = state.conferences.findIndex(c => c.isPersonal)
    if (idx === -1) {
      state.conferences.splice(0)
      return
    }
    state.conferences.splice(0, idx)
    state.conferences.splice(1)
  },

  UPDATE_PERSONAL_CONF (state, config) {
    const pc = state.conferences.find(c => c.isPersonal)
    if (!pc) {
      dbg('Failed to find personal conference to update')
      return
    }
    pc.pin = config.pin
    pc.maxusers = config.maxusers
    pc.disabled = config.disabled
    pc.opts = config.opts
  }
}

const state = {
  conferences: [],
  watched: [],
  memPaneShow: false,
  activeRoom: null,
  pagination: {
    perPage: 8,
    pages: 0,
    curPage: 0,
    start: 0,
    total: 0
  }
}

const getters = {
  rooms: state => state.conferences.filter(c => !c.isPersonal),
  watched: state => state.watched,
  personal: state => state.conferences.find(c => c.isPersonal),
  memPaneShow: state => state.memPaneShow,
  pagination: state => state.pagination,
  activeRoom: state => state.activeRoom || {},
  isConferenceWatched: state => room => state.watched.findIndex(b => b.id === room.id) !== -1
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
